import React, { useState, useRef, useEffect } from "react";
import { X, Send, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function genSessionId() {
  return "td_" + Math.random().toString(36).slice(2, 10);
}

export default function TodomonChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "bot", text: "¡Hola! Soy Todemon, tu asistente de Todemu. ¿En que puedo ayudarte?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(genSessionId);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chatbot/message`, {
        message: text,
        session_id: sessionId,
      });
      setMsgs((m) => [...m, { role: "bot", text: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "bot", text: "Ups, algo fallo. Intenta de nuevo." }]);
    }
    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          data-testid="todemon-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-5 z-50 w-16 h-16 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 overflow-hidden border-2 border-teal-400 bg-white"
          title="Habla con Todemon"
        >
          <img src="/todemon.png" alt="Todemon" className="w-full h-full object-cover" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          data-testid="todemon-chat-window"
          className="fixed bottom-20 right-5 z-50 w-[360px] max-h-[520px] flex flex-col rounded-2xl shadow-2xl border border-slate-200 overflow-hidden bg-white"
          style={{ animation: "slideUp .25s ease-out" }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-slate-900 text-white shrink-0">
            <img src="/todemon.png" alt="Todemon" className="w-9 h-9 rounded-full border border-teal-400" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm leading-tight">Todemon</p>
              <p className="text-[11px] text-teal-300">Asistente Todemu</p>
            </div>
            <button
              data-testid="todemon-close-btn"
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-slate-700 transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50" style={{ minHeight: 280, maxHeight: 380 }}>
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`todemon-msg-${i}`}
                  className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-teal-600 text-white rounded-br-sm"
                      : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm shadow-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-xl px-4 py-2 shadow-sm">
                  <Loader2 size={16} className="animate-spin text-teal-500" />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 px-3 py-2 border-t border-slate-200 bg-white shrink-0">
            <input
              ref={inputRef}
              data-testid="todemon-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Escribe tu pregunta..."
              className="flex-1 text-sm px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:border-teal-400 bg-slate-50"
              disabled={loading}
            />
            <button
              data-testid="todemon-send-btn"
              onClick={send}
              disabled={loading || !input.trim()}
              className="p-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40 transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
